import { useRef, useState } from 'react'
import { api } from '../api/client'
import type { FileFamily, ProjectDocument } from '../api/types'
import { sizeLabel } from '../lib/projectChat'
import { BADGE, FAMILY_LABEL, FAMILY_ORDER, documentRefusal, filterDocs } from '../lib/projectFiles'
import { formatDateTime } from '../lib/time'
import './documents.css'

const message = (failed: unknown) => (failed instanceof Error ? failed.message : 'Something went wrong.')

interface Upload { name: string; progress: number; state: 'sending' | 'done' | 'refused'; note?: string }

/** The type badge shared by Documents, Sensor Docs and Sensor info. */
export function FileBadge({ family }: { family: FileFamily }) {
  return <span className={`file-badge is-${family}`} aria-hidden="true">{BADGE[family]}</span>
}

/** Open (a real PDF or a plain image, in a new tab) and Download. */
export function FileLinks({ name, url, inline }: { name: string; url: string; inline: boolean }) {
  return (
    <span className="file-links">
      {inline && <a href={url} target="_blank" rel="noreferrer">Open<span className="visually-hidden"> {name}</span></a>}
      <a href={url} download={name}>Download<span className="visually-hidden"> {name}</span></a>
    </span>
  )
}

/** What one upload came to: refused with the server's sentence, or kept,
 *  with a note to say so ("Attached to 100 sensors"). */
export type Sent = { refused: string } | { note?: string }

/** Upload each file in turn with its own progress, refusing before sending
 *  what the server would refuse. Shared by Documents and Sensor Docs. */
export function useUploads() {
  const [uploads, setUploads] = useState<Upload[]>([])
  const run = async (files: File[],
                     send: (file: File, onProgress: (f: number) => void) => Promise<Sent>) => {
    const rows: Upload[] = files.map((f) => {
      const refusal = documentRefusal(f)
      return { name: f.name, progress: 0, state: refusal ? 'refused' : 'sending', note: refusal ?? undefined }
    })
    setUploads(rows)
    const set = (i: number, patch: Partial<Upload>) =>
      setUploads((now) => now.map((u, k) => (k === i ? { ...u, ...patch } : u)))
    for (const [i, file] of files.entries()) {
      if (rows[i]!.state === 'refused') continue
      try {
        const sent = await send(file, (fraction) => set(i, { progress: fraction }))
        set(i, 'refused' in sent ? { state: 'refused', note: sent.refused }
          : { state: 'done', progress: 1, note: sent.note })
      } catch (failed) {
        set(i, { state: 'refused', note: message(failed) })
      }
    }
  }
  return { uploads, run, clear: () => setUploads([]) }
}

export function UploadList({ uploads, doneText = 'Added' }: { uploads: Upload[]; doneText?: string }) {
  if (uploads.length === 0) return null
  return (
    <ul className="pi-uploads" aria-label="Uploads" aria-live="polite">
      {uploads.map((u, i) => (
        <li key={`${u.name}-${i}`} className={`pi-upload is-${u.state}`}>
          <span className="pi-upload-name">{u.name}</span>
          {u.state === 'sending' && <progress max={1} value={u.progress} aria-label={`Uploading ${u.name}`} />}
          <span className="pi-upload-note">
            {u.state === 'sending' ? `${Math.round(u.progress * 100)}%` : u.state === 'done' ? (u.note ?? doneText) : u.note}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** A project's own files (the Documents tab): reports, studies, drawings,
 *  anything but a program. Everyone in the project reads them; its Editors
 *  and Admins add them. They never reach a sensor. */
export function ProjectDocuments({ projectId, canUpload: mayUpload, documents, loadError, onChanged }: {
  projectId: number; canUpload: boolean; documents: ProjectDocument[] | undefined
  loadError?: string; onChanged: () => void
}) {
  const demo = import.meta.env.VITE_DEMO_MODE === 'true'
  const canUpload = mayUpload && !demo
  const [query, setQuery] = useState('')
  const [family, setFamily] = useState<FileFamily | ''>('')
  const [confirming, setConfirming] = useState<number | null>(null)
  const [error, setError] = useState<string>()
  const [over, setOver] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const { uploads, run } = useUploads()
  const shown = filterDocs(documents ?? [], query, family)

  const upload = async (list: FileList | null) => {
    if (!list?.length) return
    setError(undefined)
    await run(Array.from(list), async (file, onProgress): Promise<Sent> => {
      const result = await api.projects.uploadDocument(projectId, file, onProgress)
      const refused = result.refused[0]?.reason
      return refused ? { refused } : {}
    })
    onChanged()
  }

  const remove = async (doc: ProjectDocument) => {
    try {
      setError(undefined)
      await api.projects.removeDocument(projectId, doc.id)
      setConfirming(null)
      onChanged()
    } catch (failed) {
      setError(message(failed))
    }
  }

  return (
    <div role="tabpanel" aria-label="Documents" className="pd-panel">
      {demo && <p className="muted">Document uploads are available when connected to the file service.</p>}
      <div className="pi-tools">
        <label className="field inline pd-search">
          <span className="visually-hidden">Search documents</span>
          <input type="search" placeholder="Search documents" value={query}
                 onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label className="field inline">
          <span className="visually-hidden">Type</span>
          <select value={family} onChange={(e) => setFamily(e.target.value as FileFamily | '')}>
            <option value="">All types</option>
            {FAMILY_ORDER.map((f) => <option key={f} value={f}>{FAMILY_LABEL[f]}</option>)}
          </select>
        </label>
        <span className="pd-grow" />
        {canUpload && (
          <button type="button" className="button primary" onClick={() => input.current?.click()}>
            Upload documents
          </button>
        )}
        <input ref={input} type="file" multiple hidden
               onChange={(e) => { void upload(e.target.files); e.target.value = '' }} />
      </div>

      {canUpload && (
        <div className={`pd-drop${over ? ' is-over' : ''}`}
             onDragEnter={(e) => { e.preventDefault(); setOver(true) }}
             onDragOver={(e) => { e.preventDefault(); setOver(true) }}
             onDragLeave={() => setOver(false)}
             onDrop={(e) => { e.preventDefault(); setOver(false); void upload(e.dataTransfer.files) }}>
          <strong>Drop files here</strong> or use Upload documents. Any file up to 200 MB.
          <span className="muted"> Everyone in the project can see them.</span>
        </div>
      )}

      <UploadList uploads={uploads} />
      {error && <p className="error" role="alert">{error}</p>}

      {documents === undefined && (loadError
        ? <p className="error" role="alert">The documents could not be loaded: {loadError}</p>
        : <p className="muted">Loading…</p>)}
      {documents !== undefined && documents.length === 0 && (
        <p className="pj-empty">No documents yet.{canUpload ? ' Upload the first one.' : ''}</p>
      )}
      {documents !== undefined && documents.length > 0 && shown.length === 0 && (
        <p className="pj-empty">No document matches.</p>
      )}
      {shown.length > 0 && (
        <div className="pd-table">
          <table className="grid">
            <thead>
              <tr><th scope="col">Name</th><th scope="col">Type</th><th scope="col">Size</th>
                <th scope="col">Added</th><th scope="col"><span className="visually-hidden">Actions</span></th></tr>
            </thead>
            <tbody>
              {shown.map((doc) => (
                <tr key={doc.id}>
                  <td><span className="pd-name"><FileBadge family={doc.family} />{doc.name}</span></td>
                  <td className="muted">{FAMILY_LABEL[doc.family]}</td>
                  <td className="muted pd-num">{sizeLabel(doc.bytes)}</td>
                  <td className="muted">{formatDateTime(doc.created_at)}, {doc.by}</td>
                  <td>
                    <span className="pd-actions">
                      <FileLinks name={doc.name} url={doc.urls.original} inline={doc.inline} />
                      {doc.can_remove && confirming !== doc.id && (
                        <button type="button" className="link" onClick={() => setConfirming(doc.id)}>
                          Remove<span className="visually-hidden"> {doc.name}</span>
                        </button>
                      )}
                      {doc.can_remove && confirming === doc.id && (
                        <>
                          <button type="button" className="link danger" onClick={() => void remove(doc)}>
                            Remove {doc.name}
                          </button>
                          <button type="button" className="link" onClick={() => setConfirming(null)}>Cancel</button>
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
